import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Flag, Ban, CheckCircle2, Filter, ArrowLeft, AlertOctagon, ShieldCheck, Plus, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const initials = (n) => (n || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();

export default function AdminReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmBan, setConfirmBan] = useState(null); // {userId, name}

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/reports", { params: { status } }),
      ]);
      setStats(s.data);
      setReports(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load moderation queue");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      toast.success("Marked resolved");
      setReports(rs => rs.filter(r => r.id !== id));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Resolve failed"); }
    finally { setBusyId(null); }
  };

  const ban = async (userId, reason) => {
    setBusyId(`ban-${userId}`);
    try {
      await api.post(`/admin/users/${userId}/ban`, { reason: reason || "Community guidelines violation" });
      toast.success("User banned. Open reports auto-resolved.");
      setReports(rs => rs.filter(r => r.target_id !== userId));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ban failed"); }
    finally { setBusyId(null); setConfirmBan(null); }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-footy-bg text-white flex items-center justify-center px-6">
        <div className="footy-card p-8 text-center max-w-sm">
          <ShieldAlert className="mx-auto text-footy-red mb-3" />
          <h3 className="font-display text-2xl uppercase">Admin only</h3>
          <p className="text-slate-400 mt-2 text-sm">This dashboard is restricted to moderators.</p>
          <Link to="/discover" className="footy-btn-primary mt-6 inline-flex">Back to app</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-footy-bg text-white grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Link to="/discover" className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="admin-back"><ArrowLeft size={20} /></Link>
            <h1 className="font-display text-3xl uppercase tracking-wider">Moderation</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/analytics" className="text-xs uppercase tracking-widest text-slate-300 hover:text-white border-b border-transparent hover:border-footy-red pb-0.5" data-testid="admin-goto-analytics">Analytics →</Link>
            <Link to="/admin/feedback" className="text-xs uppercase tracking-widest text-slate-300 hover:text-white border-b border-transparent hover:border-footy-red pb-0.5" data-testid="admin-goto-feedback">Feedback →</Link>
            <span className="text-xs uppercase tracking-widest text-footy-red font-bold">Admin</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 pt-6 pb-24">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-stats">
            <Stat label="Users" value={stats.users} />
            <Stat label="Verified" value={stats.verified} tone="emerald" />
            <Stat label="Open reports" value={stats.reports_open} tone={stats.reports_open > 0 ? "red" : "slate"} />
            <Stat label="Banned" value={stats.banned} tone="slate" />
          </div>
        )}

        {/* Suggested new clubs — pending admin review */}
        <SuggestedClubs />

        {/* Filter */}
        <div className="mt-8 flex items-center gap-2" data-testid="admin-filter">
          <Filter size={14} className="text-slate-400" />
          {["open", "resolved", "all"].map(s => (
            <button key={s} onClick={()=>setStatus(s)} data-testid={`admin-filter-${s}`}
              className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                status === s ? "bg-footy-red border-footy-red text-white"
                : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
              {s}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-6 space-y-4" data-testid="admin-report-list">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5 animate-pulse" />)}</div>
          ) : reports.length === 0 ? (
            <div className="footy-card p-10 text-center">
              <ShieldCheck className="mx-auto text-emerald-400 mb-3" />
              <h3 className="font-display text-2xl uppercase">All clear</h3>
              <p className="text-slate-400 mt-2 text-sm">Nothing in the {status} queue.</p>
            </div>
          ) : reports.map(r => (
            <ReportCard key={r.id} r={r} busyId={busyId}
              onResolve={()=>resolve(r.id)}
              onBan={()=>setConfirmBan({ userId: r.target_id, name: r.target?.name || "user", reason: r.reason })} />
          ))}
        </div>
      </div>

      {confirmBan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={()=>setConfirmBan(null)}>
          <div className="w-full max-w-sm bg-footy-card border border-footy-red/40 rounded-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="admin-ban-confirm">
            <div className="flex items-center gap-3">
              <AlertOctagon className="text-footy-red" />
              <h2 className="font-display text-2xl uppercase">Ban {confirmBan.name}?</h2>
            </div>
            <p className="text-sm text-slate-400 mt-3">They will be logged out and unable to sign in. Their existing matches, messages and content will remain but they can't create new ones. All open reports against them will auto-resolve.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={()=>setConfirmBan(null)} className="footy-btn-secondary flex-1" data-testid="admin-ban-cancel">Cancel</button>
              <button onClick={()=>ban(confirmBan.userId, confirmBan.reason)} disabled={busyId === `ban-${confirmBan.userId}`}
                className="footy-btn-primary flex-1" data-testid="admin-ban-confirm-btn">
                {busyId === `ban-${confirmBan.userId}` ? "Banning..." : "Ban user"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const toneClass = tone === "red" ? "text-footy-red" : tone === "emerald" ? "text-emerald-400" : "text-white";
  return (
    <div className="footy-card p-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</p>
      <p className={`font-display text-3xl leading-none mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}

function ReportCard({ r, busyId, onResolve, onBan }) {
  const t = r.target;
  return (
    <div className="footy-card p-5" data-testid={`admin-report-${r.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full overflow-hidden bg-white/5 flex-none border border-white/10">
          {t?.photo_url ? (
            <img src={photoSrc(t.photo_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-lg">{initials(t?.name)}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold">{t?.name || "Deleted user"}</span>
            {t?.photo_verified && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">Verified</span>}
            {t?.is_banned && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-footy-red/20 text-footy-red border border-footy-red/40">Banned</span>}
            {r.target_report_count > 1 && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-footy-red/20 text-footy-red border border-footy-red/40" title="Repeat offender">×{r.target_report_count} reports</span>}
          </div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">
            {t?.club || "—"} · {t?.location || "—"} · {new Date(r.at).toLocaleString("en-GB")}
          </p>
        </div>
        {r.status === "resolved" && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Resolved</span>
        )}
      </div>
      <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
        <p className="text-xs uppercase tracking-widest text-footy-red font-bold flex items-center gap-2"><Flag size={12} /> Reason · {r.context || "profile"}</p>
        <p className="text-sm text-slate-200 mt-2">{r.reason}</p>
        <p className="text-[11px] text-slate-500 mt-3">Reported by {r.reporter_email}</p>
      </div>
      {r.status === "open" && (
        <div className="mt-4 flex gap-3">
          <button onClick={onResolve} disabled={busyId === r.id} className="footy-btn-secondary flex-1" data-testid={`admin-resolve-${r.id}`}>
            <CheckCircle2 size={16} /> {busyId === r.id ? "..." : "Mark resolved"}
          </button>
          {!t?.is_banned && t?.id && (
            <button onClick={onBan} className="footy-btn-primary flex-1" data-testid={`admin-ban-${r.target_id}`}>
              <Ban size={16} /> Ban user
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function SuggestedClubs() {
  const [items, setItems] = useState([]);
  const [minCount, setMinCount] = useState(1);
  const [busyKey, setBusyKey] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/custom-clubs", { params: { min_count: minCount } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load suggested clubs");
    } finally { setLoaded(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minCount]);

  const dismiss = async (nameKey) => {
    setBusyKey(nameKey);
    try {
      await api.post(`/admin/custom-clubs/${encodeURIComponent(nameKey)}/dismiss`, { dismiss: true });
      setItems(prev => prev.filter(c => c.name_key !== nameKey));
      toast.success("Dismissed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Dismiss failed");
    } finally { setBusyKey(null); }
  };

  if (!loaded) return null;

  return (
    <div className="mt-8 footy-card p-5" data-testid="admin-suggested-clubs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Plus size={16} className="text-footy-red" />
          <h2 className="font-display text-2xl uppercase leading-none">Suggested new clubs</h2>
        </div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
          <span className="text-slate-400">Min users</span>
          {[1, 3, 10].map(n => (
            <button key={n} onClick={()=>setMinCount(n)} data-testid={`suggested-min-${n}`}
              className={`px-2.5 py-1 rounded-full border ${
                minCount === n ? "bg-footy-red border-footy-red text-white"
                : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>{n}+</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2">
        Users typed these clubs into the non-league field. Promote a popular one by adding it to
        <span className="text-slate-300"> /app/frontend/src/data/clubs.js</span> +
        <span className="text-slate-300"> /app/backend/known_clubs.py</span>, then dismiss it here.
      </p>

      {items.length === 0 ? (
        <p className="text-sm text-slate-400 mt-5 text-center py-3">
          {minCount > 1 ? `No clubs have ${minCount}+ users yet.` : "No custom clubs submitted yet."}
        </p>
      ) : (
        <div className="mt-5 space-y-2" data-testid="suggested-clubs-list">
          {items.map(c => (
            <div key={c.name_key} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
                 data-testid={`suggested-club-${c.name_key}`}>
              <div className="w-10 h-10 rounded-lg bg-footy-red/15 text-footy-red flex items-center justify-center font-display text-lg flex-none">
                {(c.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Users size={11} /> {c.count} user{c.count === 1 ? "" : "s"}
                  <span className="text-slate-600">·</span>
                  <span>first {new Date(c.first_at).toLocaleDateString("en-GB")}</span>
                </div>
              </div>
              {c.count >= 10 && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex-none">
                  Promote?
                </span>
              )}
              <button onClick={()=>dismiss(c.name_key)} disabled={busyKey === c.name_key}
                className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-white disabled:opacity-50"
                data-testid={`suggested-dismiss-${c.name_key}`}>
                {busyKey === c.name_key ? "..." : "Dismiss"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
